import { z } from 'zod';
import { LangCodeSchema, TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// Folder
// =============================================================================

/** Row shape for `folders`. */
export const FolderSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  parent_id: UuidSchema.nullable(),
  name: z.string().min(1).max(200),
  created_by: UuidSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Folder = z.infer<typeof FolderSchema>;

/** Input for POST /documents/folders. */
export const FolderCreateInputSchema = z.object({
  name: z.string().min(1).max(200),
  parent_id: UuidSchema.nullable().optional(),
});
export type FolderCreateInput = z.infer<typeof FolderCreateInputSchema>;

/** Input for PATCH /documents/folders/:id. */
export const FolderUpdateInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    parent_id: UuidSchema.nullable(),
  })
  .partial();
export type FolderUpdateInput = z.infer<typeof FolderUpdateInputSchema>;

/**
 * Recursive folder tree shape.
 *
 * The server returns a flat array (see useFolderTree) and the client assembles
 * this shape; the Zod schema here exists for any callers that want to validate
 * a pre-built tree payload.
 */
export interface FolderTree extends Folder {
  children: FolderTree[];
}

export const FolderTreeSchema: z.ZodType<FolderTree> = z.lazy(() =>
  FolderSchema.extend({
    children: z.array(FolderTreeSchema),
  }),
);

// =============================================================================
// Document
// =============================================================================

/** Row shape for `documents`. */
export const DocumentSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  folder_id: UuidSchema.nullable(),
  name: z.string().min(1).max(500),
  mime_type: z.string().min(1).max(200),
  size_bytes: z.number().int().nonnegative(),
  storage_key: z.string().min(1).max(1024),
  checksum_sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  uploaded_by: UuidSchema,
  description: z.string().max(4000).nullable(),
  source_lang: LangCodeSchema,
  deleted_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Document = z.infer<typeof DocumentSchema>;

/**
 * Input for POST /documents/upload-url.
 *
 * No file blob — the Edge Function creates the metadata row and returns a
 * signed URL that the client PUTs to directly.
 */
export const DocumentCreateInputSchema = z.object({
  name: z.string().min(1).max(500),
  mime_type: z.string().min(1).max(200),
  size_bytes: z
    .number()
    .int()
    .nonnegative()
    .max(5 * 1024 * 1024 * 1024), // 5 GB ceiling
  folder_id: UuidSchema.nullable().optional(),
  description: z.string().max(4000).optional(),
});
export type DocumentCreateInput = z.infer<typeof DocumentCreateInputSchema>;

/** Input for PATCH /documents/:id — rename, move, update description. */
export const DocumentUpdateInputSchema = z
  .object({
    name: z.string().min(1).max(500),
    folder_id: UuidSchema.nullable(),
    description: z.string().max(4000).nullable(),
  })
  .partial();
export type DocumentUpdateInput = z.infer<typeof DocumentUpdateInputSchema>;

// =============================================================================
// DocumentShare
// =============================================================================

export const DocumentPermissionSchema = z.enum(['view', 'edit']);
export type DocumentPermission = z.infer<typeof DocumentPermissionSchema>;

/** Row shape for `document_shares`. */
export const DocumentShareSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  document_id: UuidSchema,
  shared_with_user_id: UuidSchema.nullable(),
  shared_with_team_id: UuidSchema.nullable(),
  permission: DocumentPermissionSchema,
  created_by: UuidSchema,
  created_at: TimestampSchema,
});
export type DocumentShare = z.infer<typeof DocumentShareSchema>;

/**
 * Input for POST /documents/:id/shares.
 *
 * Exactly one of `user_id` / `team_id` must be provided — the DB constraint
 * rejects rows that set both or neither.
 */
export const DocumentShareCreateInputSchema = z
  .object({
    user_id: UuidSchema.optional(),
    team_id: UuidSchema.optional(),
    permission: DocumentPermissionSchema.default('view'),
  })
  .superRefine((val, ctx) => {
    const userSet = val.user_id !== undefined;
    const teamSet = val.team_id !== undefined;
    if (userSet === teamSet) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['user_id'],
        message: 'Exactly one of user_id or team_id is required',
      });
    }
  });
export type DocumentShareCreateInput = z.infer<typeof DocumentShareCreateInputSchema>;

// =============================================================================
// Signed-URL response envelopes
// =============================================================================

/** Response from POST /documents/upload-url. */
export const DocumentUploadUrlResponseSchema = z.object({
  upload_url: z.string().url(),
  storage_key: z.string(),
  document_id: UuidSchema,
  expires_at: TimestampSchema,
  max_bytes: z.number().int().nonnegative(),
});
export type DocumentUploadUrlResponse = z.infer<typeof DocumentUploadUrlResponseSchema>;

/** Response from GET /documents/:id/download-url. */
export const DocumentDownloadUrlResponseSchema = z.object({
  url: z.string().url(),
  expires_at: TimestampSchema,
});
export type DocumentDownloadUrlResponse = z.infer<typeof DocumentDownloadUrlResponseSchema>;
